import { PROPS_CATALOG, type CharacterProp } from '@/types/props';
import { motion } from 'motion/react';
import { useState } from 'react';
import Character3D, { type CharacterConfig } from '../game/elements/Character3D';
import Prop3D from '../game/elements/Prop3D';
import { PixelIcon } from '@/components/ui';

// Deep partial type helper
type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

// Character configurations showcase
const characterConfigs: { name: string; config: DeepPartial<CharacterConfig> }[] = [
  {
    name: 'Default Hero',
    config: {}, // Uses all defaults
  },
  {
    name: 'Tiny Blue',
    config: {
      sizes: {
        characterSize: 0.5,
        cubeSize: 0.95,
      },
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #4a90e2 0%, #357abd 50%, #2968a3 100%)',
          back: 'linear-gradient(135deg, #357abd 0%, #2968a3 50%, #1e5189 100%)',
          top: 'linear-gradient(135deg, #5ba0f2 0%, #4a90e2 50%, #357abd 100%)',
          bottom: 'linear-gradient(135deg, #2968a3 0%, #1e5189 50%, #143a5f 100%)',
          left: 'linear-gradient(135deg, #4080d0 0%, #3570c0 50%, #2a60b0 100%)',
          right: 'linear-gradient(135deg, #4585d5 0%, #3a75c5 50%, #2f65b5 100%)',
        },
      },
      face: {
        eyes: {
          size: '35%',
        },
      },
    },
  },
  {
    name: 'Chonky Green',
    config: {
      sizes: {
        characterSize: 0.95,
        cubeSize: 0.85,
        shadow: {
          width: 0.9,
          height: 0.25,
          offsetY: 0.12,
        },
      },
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #4caf50 0%, #45a049 50%, #388e3c 100%)',
          back: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 50%, #1b5e20 100%)',
          top: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 50%, #43a047 100%)',
          bottom: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 50%, #0d3f15 100%)',
          left: 'linear-gradient(135deg, #43a047 0%, #3b9040 50%, #338037 100%)',
          right: 'linear-gradient(135deg, #48a84c 0%, #409843 50%, #38883b 100%)',
        },
      },
      face: {
        eyes: {
          size: '15%',
          gap: '35%',
        },
        mouth: {
          width: '40%',
        },
      },
    },
  },
  {
    name: 'Sleek Purple',
    config: {
      sizes: {
        characterSize: 0.75,
        cubeSize: 0.98,
      },
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #9c27b0 0%, #8e24aa 50%, #7b1fa2 100%)',
          back: 'linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 50%, #4a148c 100%)',
          top: 'linear-gradient(135deg, #ab47bc 0%, #9c27b0 50%, #8e24aa 100%)',
          bottom: 'linear-gradient(135deg, #6a1b9a 0%, #4a148c 50%, #2a0a5c 100%)',
          left: 'linear-gradient(135deg, #8e24aa 0%, #7e22a0 50%, #6e1f96 100%)',
          right: 'linear-gradient(135deg, #9125ac 0%, #8123a2 50%, #712098 100%)',
        },
        effects: {
          shadow: 'radial-gradient(ellipse, rgba(156, 39, 176, 0.4) 0%, transparent 70%)',
        },
      },
      face: {
        eyes: {
          size: '22%',
          shine: {
            size: '40%',
            position: { top: '15%', left: '20%' },
          },
        },
        mouth: {
          width: '30%',
          heights: {
            neutral: '12%',
            happy: '20%',
            shocked: '25%',
          },
        },
      },
    },
  },
  {
    name: 'Golden Glow',
    config: {
      sizes: {
        characterSize: 0.8,
        cubeSize: 0.9,
      },
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #ffc107 0%, #ffb300 50%, #ffa000 100%)',
          back: 'linear-gradient(135deg, #ffa000 0%, #ff8f00 50%, #ff6f00 100%)',
          top: 'linear-gradient(135deg, #ffca28 0%, #ffc107 50%, #ffb300 100%)',
          bottom: 'linear-gradient(135deg, #ff8f00 0%, #ff6f00 50%, #e65100 100%)',
          left: 'linear-gradient(135deg, #ffb300 0%, #ffa300 50%, #ff9300 100%)',
          right: 'linear-gradient(135deg, #ffb800 0%, #ffa800 50%, #ff9800 100%)',
        },
      },
      backFace: {
        symbol: '*',
        sizeScale: 0.4,
        opacity: 0.6,
      },
    },
  },
];

const CharactersScreen: React.FC = () => {
  const [selectedConfig, setSelectedConfig] = useState(0);
  const [activeSection, setActiveSection] = useState<'characters' | 'props'>('characters');
  const [selectedProp, setSelectedProp] = useState<CharacterProp | null>(null);
  const [equippedProps, setEquippedProps] = useState<CharacterProp[]>([]);

  const cellSize = 60;

  const handlePropEquip = (prop: CharacterProp) => {
    setEquippedProps(prev => {
      // Remove any prop in the same slot
      const filtered = prev.filter(p => p.slot !== prop.slot);
      // Add the new prop
      return [...filtered, prop];
    });
  };

  const handlePropUnequip = (slot: string) => {
    setEquippedProps(prev => prev.filter(p => p.slot !== slot));
  };

  return (
    <div className="p-3 md:p-8 max-w-full md:max-w-7xl mx-auto h-full overflow-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-4xl font-bold mb-8 sticky top-0 z-10">Characters & Props</h1>

        {/* Section Tabs */}
        <div className="flex gap-2 md:gap-4 mb-8 sticky top-12 z-10 flex-wrap">
          <button
            onClick={() => setActiveSection('characters')}
            className={`px-3 py-2 md:px-6 md:py-3 rounded-lg font-semibold transition-all text-sm md:text-base ${
              activeSection === 'characters'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Characters
          </button>
          <button
            onClick={() => setActiveSection('props')}
            className={`px-3 py-2 md:px-6 md:py-3 rounded-lg font-semibold transition-all text-sm md:text-base ${
              activeSection === 'props'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Props & Equipment
          </button>
        </div>

        {/* Characters Section */}
        {activeSection === 'characters' && (
          <>
            {/* Character Carousel */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Character Gallery</h2>
              <div className="relative bg-gray-800 rounded-lg p-3 md:p-8 overflow-hidden">
                <div className="flex items-center justify-center gap-3 md:gap-8">
                  {characterConfigs.map((char, index) => (
                    <motion.div
                      key={index}
                      className={`cursor-pointer transition-all ${
                        index === selectedConfig ? 'scale-110' : 'scale-90 opacity-50'
                      }`}
                      onClick={() => setSelectedConfig(index)}
                      whileHover={{ scale: index === selectedConfig ? 1.1 : 0.95 }}
                    >
                      <div className="flex flex-col items-center">
                        <div className="bg-gray-900 rounded-lg p-4 mb-2">
                          <div style={{ width: cellSize, height: cellSize, position: 'relative' }}>
                            <Character3D
                              position={[0, 0]}
                              direction={null}
                              isMoving={false}
                              cellSize={cellSize}
                              gameState="playing"
                              config={char.config}
                            />
                          </div>
                        </div>
                        <p className="text-sm font-medium">{char.name}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Navigation arrows */}
                <button
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-gray-700 hover:bg-gray-600 rounded-full p-2 transition-colors"
                  onClick={() =>
                    setSelectedConfig(prev => (prev > 0 ? prev - 1 : characterConfigs.length - 1))
                  }
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <button
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-gray-700 hover:bg-gray-600 rounded-full p-2 transition-colors"
                  onClick={() =>
                    setSelectedConfig(prev => (prev < characterConfigs.length - 1 ? prev + 1 : 0))
                  }
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Character Details */}
            <motion.div
              key={selectedConfig}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-gray-800 rounded-lg p-6 sticky top-32 z-10"
            >
              <h2 className="text-2xl font-bold mb-4">
                {characterConfigs[selectedConfig].name} Configuration
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Live Preview */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Live Preview</h3>
                  <div className="bg-gray-900 rounded-lg p-8 flex items-center justify-center">
                    <div
                      style={{ width: cellSize * 2, height: cellSize * 2, position: 'relative' }}
                    >
                      <Character3D
                        position={[0, 0]}
                        direction={null}
                        isMoving={false}
                        cellSize={cellSize * 2}
                        gameState="playing"
                        config={characterConfigs[selectedConfig].config}
                      />
                    </div>
                  </div>
                </div>

                {/* Configuration Details */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Configuration Details</h3>
                  <pre className="bg-gray-900 rounded-lg p-4 text-sm overflow-auto max-h-96">
                    {JSON.stringify(characterConfigs[selectedConfig].config, null, 2)}
                  </pre>
                </div>
              </div>
            </motion.div>
          </>
        )}

        {/* Props Section */}
        {activeSection === 'props' && (
          <div className="space-y-8">
            {/* Character with Props Preview */}
            <div className="bg-gray-800 rounded-lg p-6 sticky top-32 z-10">
              <h2 className="text-2xl font-bold mb-4">Character with Props</h2>
              <div className="grid md:grid-cols-2 gap-8">
                {/* Character Preview */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Preview</h3>
                  <div className="bg-gray-900 rounded-lg p-8 flex items-center justify-center">
                    <div
                      style={{ width: cellSize * 3, height: cellSize * 3, position: 'relative' }}
                    >
                      <Character3D
                        position={[0, 0]}
                        direction={null}
                        isMoving={false}
                        cellSize={cellSize * 3}
                        gameState="playing"
                        equippedProps={equippedProps}
                      />
                    </div>
                  </div>

                  {/* Equipped Props List */}
                  <div className="mt-4">
                    <h4 className="font-semibold mb-2">Equipped Props:</h4>
                    <div className="space-y-2">
                      {equippedProps.length === 0 ? (
                        <p className="text-gray-400">No props equipped</p>
                      ) : (
                        equippedProps.map(prop => (
                          <div
                            key={prop.id}
                            className="flex items-center justify-between bg-gray-700 rounded p-2"
                          >
                            <span>
                              {prop.name} ({prop.slot})
                            </span>
                            <button
                              onClick={() => handlePropUnequip(prop.slot)}
                              className="text-red-400 hover:text-red-300"
                            >
                              Unequip
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Prop Details */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Selected Prop Details</h3>
                  {selectedProp ? (
                    <div className="bg-gray-900 rounded-lg p-4 space-y-4">
                      <div>
                        <h4 className="text-xl font-bold">{selectedProp.name}</h4>
                        <p className="text-sm text-gray-400">
                          {selectedProp.rarity} • {selectedProp.slot}
                        </p>
                      </div>

                      <p className="text-gray-300">{selectedProp.description}</p>

                      <div>
                        <h5 className="font-semibold mb-2">Effects:</h5>
                        <ul className="space-y-1">
                          {selectedProp.effects.map((effect, index) => (
                            <li key={index} className="text-sm text-blue-400">
                              • {effect.description}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h5 className="font-semibold mb-2">Price:</h5>
                        <div className="flex gap-4">
                          {selectedProp.price.coins && (
                            <span className="text-yellow-400">
                              {selectedProp.price.coins} coins
                            </span>
                          )}
                          {selectedProp.price.gems && (
                            <span className="text-purple-400">{selectedProp.price.gems} gems</span>
                          )}
                        </div>
                      </div>

                      <button
                        onClick={() => handlePropEquip(selectedProp)}
                        className="w-full py-2 bg-blue-600 hover:bg-blue-700 rounded font-semibold transition-colors"
                      >
                        Equip Prop
                      </button>
                    </div>
                  ) : (
                    <div className="bg-gray-900 rounded-lg p-4 text-center text-gray-400">
                      Select a prop to view details
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Props Catalog */}
            <div className="bg-gray-800 rounded-lg p-6 sticky top-32">
              <h2 className="text-2xl font-bold mb-4">Props Catalog</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {PROPS_CATALOG.map(prop => (
                  <motion.div
                    key={prop.id}
                    className={`bg-gray-900 rounded-lg p-4 cursor-pointer transition-all ${
                      selectedProp?.id === prop.id ? 'ring-2 ring-blue-500' : ''
                    }`}
                    onClick={() => setSelectedProp(prop)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {/* Prop Preview */}
                    <div className="bg-gray-800 rounded p-4 mb-3 flex items-center justify-center">
                      <div
                        style={{
                          width: cellSize * 1.5,
                          height: cellSize * 1.5,
                          position: 'relative',
                        }}
                      >
                        <Prop3D prop={prop} size={cellSize * 1.5} />
                      </div>
                    </div>

                    {/* Prop Info */}
                    <h3 className="font-semibold text-sm mb-1">{prop.name}</h3>
                    <p className="text-xs text-gray-400 capitalize">
                      {prop.rarity} • {prop.slot}
                    </p>

                    {/* Price */}
                    <div className="mt-2 text-xs">
                      {prop.price.coins && (
                        <span className="text-yellow-400"><PixelIcon name="coin" size={14} color="#FFD700" /> {prop.price.coins}</span>
                      )}
                      {prop.price.gems && (
                        <span className="text-purple-400 ml-2"><PixelIcon name="gem" size={14} color="#9C27B0" /> {prop.price.gems}</span>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Rarity Legend */}
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-3">Rarity Tiers</h3>
              <div className="flex gap-6 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gray-500 rounded"></div>
                  <span className="text-sm">Common</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-500 rounded"></div>
                  <span className="text-sm">Rare</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-purple-500 rounded"></div>
                  <span className="text-sm">Epic</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-orange-500 rounded"></div>
                  <span className="text-sm">Legendary</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default CharactersScreen;
